import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, forkJoin, of } from 'rxjs';

import { AuthService } from '../../../core/auth/auth.service';
import { PageHeaderComponent } from '../../../shared/ui/page-header/page-header.component';
import {
  CreateSpecialistDowntimeRequest,
  SpecialistDowntimeImpactPreview,
  SpecialistDowntimeItem
} from '../data-access/specialist-downtime.models';
import { SpecialistDowntimeService } from '../data-access/specialist-downtime.service';
import { SpecialistDowntimeQuickViewModalComponent } from '../components/specialist-downtime-quick-view-modal.component';
import {
  SpecialistDowntimeClinicOption,
  SpecialistDowntimeFormComponent,
} from '../components/specialist-downtime-form.component';
import { DowntimeImpactSummaryComponent } from '../../downtime/components/downtime-impact-summary.component';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { LoadingBlockComponent } from '../../../shared/ui/loading-block/loading-block.component';
import { ErrorBlockComponent, PageShellComponent } from '@clinic-platform/ui-components';
import { SettingsService } from '../../settings/data-access/settings.service';
import { ClinicOperationalSettingsDto } from '../../settings/data-access/settings.models';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';

@Component({
  selector: 'app-specialist-downtime-create-page',
  standalone: true,
  imports: [
    PageShellComponent,
    ErrorBlockComponent,
    PageHeaderComponent,
    SpecialistDowntimeFormComponent,
    DowntimeImpactSummaryComponent,
    SpecialistDowntimeQuickViewModalComponent,
    LoadingBlockComponent,
    TranslatePipe
  ],
  template: `
    <lib-page-shell>
      <app-page-header
        title="Create Specialist Downtime"
        subtitle="Manual specialist unavailability registration"
      />

      @if (createdDowntime) {
        <app-specialist-downtime-quick-view-modal
          [isOpen]="true"
          [item]="createdDowntime"
          [clinics]="clinicOptions"
          [specialists]="specialistOptions"
          (close)="closeCreatedDowntime()"
        />
      }

      @if (!createdDowntime && previewImpact && pendingCreatePayload) {
        <app-downtime-impact-summary
          [affectedAppointmentIds]="previewImpact.affectedAppointmentIds"
          [reachabilityImpact]="previewImpact.reachabilityImpact"
        />

        <div class="downtime-create-page__preview-actions">
          <button
            type="button"
            class="downtime-create-page__secondary"
            [disabled]="isSubmitting"
            (click)="backToForm()"
          >
            {{ 'Back to edit' | t }}
          </button>
          <button
            type="button"
            class="downtime-create-page__primary"
            [disabled]="isSubmitting"
            (click)="confirmCreateDowntime()"
          >
            {{ (isSubmitting ? 'Creating...' : 'Create downtime') | t }}
          </button>
        </div>
      }

      @if (errorText) {
        <lib-error-block variant="error" [message]="errorText" />
      }

      @if (referenceOptionsLoading) {
        <app-loading-block text="Loading specialist context..." />
      } @else if (!previewImpact && !createdDowntime) {
        <app-specialist-downtime-form
          [isSubmitting]="isSubmitting"
          [errorText]="null"
          [clinics]="clinicOptions"
          [specialists]="specialistOptions"
          [initialValue]="pendingCreatePayload"
          (submitted)="onSubmitted($event)"
        />
      }
    </lib-page-shell>
  `,
  styles: [`
    .downtime-create-page__preview-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      margin: 16px 0;
    }

    .downtime-create-page__secondary,
    .downtime-create-page__primary {
      min-height: 40px;
      border-radius: 10px;
      padding: 0 14px;
      font-weight: 600;
      cursor: pointer;
    }

    .downtime-create-page__secondary {
      border: 1px solid #d1d5db;
      background: #ffffff;
      color: #334155;
    }

    .downtime-create-page__primary {
      border: none;
      background: #2563eb;
      color: #ffffff;
    }

    @media (max-width: 640px) {
      .downtime-create-page__preview-actions {
        flex-direction: column;
      }

      .downtime-create-page__secondary,
      .downtime-create-page__primary {
        width: 100%;
      }
    }
  `]
})
export class SpecialistDowntimeCreatePageComponent
{
  private readonly downtimeService = inject(SpecialistDowntimeService);
  private readonly settingsService = inject(SettingsService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  protected isSubmitting = false;
  protected errorText: string | null = null;
  protected createdDowntime: SpecialistDowntimeItem | null = null;
  protected previewImpact: SpecialistDowntimeImpactPreview | null = null;
  protected pendingCreatePayload: CreateSpecialistDowntimeRequest | null = null;
  protected referenceOptionsLoading = false;
  protected clinicOptions: SpecialistDowntimeClinicOption[] = [];
  protected specialistOptions: Array<{ id: string; label: string; clinicId: string | null }> = [];

  public constructor()
  {
    this.loadReferenceOptions();
  }

  protected onSubmitted(payload: CreateSpecialistDowntimeRequest): void
  {
    if (this.isSubmitting || this.createdDowntime) {
      return;
    }
    this.isSubmitting = true;
    this.errorText = null;
    this.createdDowntime = null;
    this.previewImpact = null;
    this.pendingCreatePayload = null;

    this.downtimeService.previewImpact(payload).subscribe({
      next: (preview: SpecialistDowntimeImpactPreview) =>
      {
        this.previewImpact = preview;
        this.pendingCreatePayload = payload;
        this.isSubmitting = false;
      },
      error: (error: unknown) =>
      {
        console.error('Failed to preview specialist downtime impact', error);
        this.errorText = this.resolveCreateErrorText(
          error,
          'Backend rejected specialist downtime impact preview request.',
        );
        this.isSubmitting = false;
      }
    });
  }

  protected backToForm(): void
  {
    if (this.isSubmitting) {
      return;
    }

    this.previewImpact = null;
    this.errorText = null;
  }

  protected confirmCreateDowntime(): void
  {
    if (this.isSubmitting || this.createdDowntime || !this.pendingCreatePayload) {
      return;
    }

    this.createDowntime(this.pendingCreatePayload);
  }

  private createDowntime(payload: CreateSpecialistDowntimeRequest): void
  {
    this.isSubmitting = true;
    this.errorText = null;
    this.createdDowntime = null;

    this.downtimeService.create(payload).subscribe({
      next: (downtime: SpecialistDowntimeItem) =>
      {
        this.createdDowntime = downtime;
        this.previewImpact = null;
        this.pendingCreatePayload = null;
        this.isSubmitting = false;

        this.downtimeService.getById(downtime.id).subscribe({
          next: (detail) => {
            if (this.createdDowntime?.id === detail.id) {
              this.createdDowntime = detail;
            }
          },
          error: (error: unknown) => console.error('Failed to load created specialist downtime detail', error),
        });
      },
      error: (error: unknown) =>
      {
        console.error('Failed to create specialist downtime', error);
        this.errorText = this.resolveCreateErrorText(
          error,
          'Backend rejected specialist downtime creation request.',
        );
        this.isSubmitting = false;
      }
    });
  }

  protected closeCreatedDowntime(): void
  {
    void this.router.navigate(this.withTenantPath(['downtime', 'specialist']));
  }

  private resolveCreateErrorText(error: unknown, fallback: string): string
  {
    return resolveHttpErrorMessageKey(error, {
      forbidden: 'error.forbiddenDowntimeCreate',
      badRequestByMessage: {
        'startTime must align to 15-minute slot grid': 'error.downtimeStartSlotGrid',
        'expectedRepairUntil must align to 15-minute slot grid': 'error.downtimeEndSlotGrid',
        'expectedRepairUntil must be greater than startTime': 'error.downtimeEndAfterStart',
        'Invalid downtime time range': 'error.downtimeInvalidTimeRange',
      },
      fallback
    });
  }

  private loadReferenceOptions(): void
  {
    this.referenceOptionsLoading = true;
    forkJoin({
      context: this.downtimeService.getCreateContext(),
      clinicSettings: this.settingsService.getClinicOperationalSettings().pipe(
        catchError(() => of([] as ClinicOperationalSettingsDto[])),
      ),
    }).subscribe({
      next: ({ context, clinicSettings }) => {
        this.clinicOptions = this.mergeClinicSettings(context.clinics, clinicSettings);
        this.specialistOptions = context.specialists;
        this.referenceOptionsLoading = false;
      },
      error: () => {
        this.clinicOptions = [];
        this.specialistOptions = [];
        this.referenceOptionsLoading = false;
      },
    });
  }

  private mergeClinicSettings(
    clinics: Array<{ id: string; label: string }>,
    settings: ClinicOperationalSettingsDto[],
  ): SpecialistDowntimeClinicOption[]
  {
    const settingsByClinicId = new Map(settings.map((item) => [item.clinicId, item]));

    return clinics.map((clinic) =>
    {
      const clinicSettings = settingsByClinicId.get(clinic.id);
      return {
        ...clinic,
        workingDayStart: clinicSettings?.workingDayStart ?? null,
        workingDayEnd: clinicSettings?.workingDayEnd ?? null,
        slotDurationMinutes: clinicSettings?.effectiveSlotDurationMinutes ?? null,
      };
    });
  }

  private withTenantPath(segments: Array<string>): Array<string>
  {
    const role = this.authService.getCurrentRole();
    const slug = this.authService.getPreferredCompanySlug();

    if (role !== 'system_admin' && slug)
    {
      return ['/', slug, ...segments];
    }

    return ['/', ...segments];
  }
}
